import type { SalesMetricType } from '@api';
import type { EChartsCoreOption } from 'echarts/core';

type GetSeriesOptionsThemeColorsType = {
  lineColor: string;
  barColor: string;
  selectedLineColor: string;
  selectedBarColor: string;
};

type GetSeriesOptionsArgsType = {
  salesTimeline: SalesMetricType[];
  themeColors: GetSeriesOptionsThemeColorsType;
  selectedDate1: string | null;
  selectedDate2: string | null;
};

type GetSeriesOptionsReturnType = EChartsCoreOption['series'];

const isDateHighlighted = (
  date: string,
  selectedDate1: string | null,
  selectedDate2: string | null
): boolean => {
  if (!selectedDate1) {
    return false;
  }

  if (!selectedDate2) {
    return date === selectedDate1;
  }

  const rangeStart =
    selectedDate1 <= selectedDate2 ? selectedDate1 : selectedDate2;
  const rangeEnd =
    selectedDate1 <= selectedDate2 ? selectedDate2 : selectedDate1;

  return date >= rangeStart && date <= rangeEnd;
};

export const getSeriesOptions = ({
  salesTimeline,
  themeColors,
  selectedDate1,
  selectedDate2,
}: GetSeriesOptionsArgsType): GetSeriesOptionsReturnType => {
  const hasSelection = Boolean(selectedDate1);
  const isSingleSelection = Boolean(selectedDate1 && !selectedDate2);

  return [
    {
      type: 'line',
      yAxisIndex: 0,
      data: salesTimeline.map(({ salesTotal }) => salesTotal),
      showSymbol: false,
      lineStyle: {
        width: 1.5,
        color: themeColors.lineColor,
      },
    },
    ...(hasSelection
      ? [
          {
            type: 'line' as const,
            yAxisIndex: 0,
            data: salesTimeline.map(({ salesTotal, date }) =>
              isDateHighlighted(date, selectedDate1, selectedDate2)
                ? salesTotal
                : null
            ),
            showSymbol: isSingleSelection,
            symbolSize: isSingleSelection ? 8 : 0,
            connectNulls: false,
            lineStyle: {
              width: 2,
              color: themeColors.selectedLineColor,
            },
            itemStyle: {
              color: themeColors.selectedLineColor,
            },
            z: 10,
          },
        ]
      : []),
    {
      type: 'bar',
      yAxisIndex: 1,
      data: salesTimeline.map(({ salesIncrease, date }) => ({
        value: salesIncrease,
        itemStyle: {
          color: isDateHighlighted(date, selectedDate1, selectedDate2)
            ? themeColors.selectedBarColor
            : themeColors.barColor,
        },
      })),
      barMaxWidth: 8,
    },
  ];
};
